import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ShieldCheck,
  ArrowRight,
  LockKeyhole,
  Library,
  HeartHandshake,
  Footprints,
  Users,
  BookOpen,
  HeartPulse,
  Leaf,
  Globe,
  Shield,
  Instagram,
  Scale,
} from 'lucide-react';
import QuickExitButton from '../components/QuickExitButton';
import FloatingQuickExit from '../components/FloatingQuickExit';
import AccessibilityFAB from '../components/AccessibilityFAB';
import SEO from '../components/SEO';
import ShareCard from '../components/ShareCard';
import TranslateHelper from '../components/TranslateHelper';

const HERO_IMAGE =
  'https://static.prod-images.emergentagent.com/jobs/e4a56433-de33-48f0-9e84-678bcf45ff81/images/b7f8e511ce709b5c36d9fd28200b3149823628b0bc84c5b81888df044aeae659.png';

const SV_DOORWAYS = [
  {
    to: '/app/crossed-the-line',
    title: 'Was that assault?',
    body: 'A calm guide for anyone unsure whether what happened counts, with quiet answers to the "but…" thoughts that stop survivors naming it.',
    icon: HeartHandshake,
  },
  {
    to: '/app/process',
    title: 'The Judicial Process',
    body: 'Step-by-step through reporting, investigation, CPS, court, verdict, sentencing and appeals — every stage explained gently.',
    icon: Footprints,
  },
  {
    to: '/app/rights',
    title: 'Your Rights',
    body: 'Lifetime anonymity, ISVA support, special measures in court, Victim Personal Statements — all 10 rights under UK law.',
    icon: ShieldCheck,
  },
  {
    to: '/app/support?tab=sv',
    title: 'Sexual-violence support',
    body: 'Rape Crisis, New Pathways, Survivors Trust, SARCs, Samaritans, Galop, Safeline — UK helplines you can call today.',
    icon: Users,
  },
];

const DA_DOORWAYS = [
  {
    to: '/app/crossed-the-line',
    title: 'Is this abuse?',
    body: 'A gentle guide to coercive control, emotional harm, financial abuse and digital monitoring — with quiet answers to the doubts that hold you back.',
    icon: HeartHandshake,
  },
  {
    to: '/app/domestic-abuse#process',
    title: 'DA Judicial Process',
    body: 'From recognising abuse and safety planning, through reporting, DAPN/DAPO, MARAC, CPS, court and after the case.',
    icon: Footprints,
  },
  {
    to: '/app/domestic-abuse#rights',
    title: 'Your DA Rights',
    body: 'The Domestic Abuse Act 2021, coercive-control offence, protective orders, legal aid, Clare\u2019s Law, refuge and IDVA support.',
    icon: ShieldCheck,
  },
  {
    to: '/app/domestic-abuse#types',
    title: 'Types of abuse',
    body: 'The seven UK-recognised forms — coercive, physical, sexual, financial, emotional, stalking/tech, and "honour"-based abuse.',
    icon: Shield,
  },
  {
    to: '/app/domestic-abuse#safety',
    title: 'Safety planning',
    body: 'A personal plan for while you\u2019re still living together, a "go bag", leaving safely, and after you\u2019ve left.',
    icon: HeartHandshake,
  },
  {
    to: '/app/support?tab=da',
    title: 'DA support',
    body: 'Refuge 24/7 helpline, Women\u2019s Aid, Men\u2019s Advice Line, Karma Nirvana, NCDV, Galop, Hourglass, Respect Phoneline.',
    icon: Users,
  },
];

const SHARED_TOOLS = [
  {
    to: '/app/consent',
    title: 'Consent — UK law',
    body: 'Plain-language guide across England, Wales, Scotland and Northern Ireland. Freezing is not consent.',
    icon: Scale,
  },
  {
    to: '/app/journal',
    title: 'Private Journal',
    body: 'A calm place to write — with moods, stored only in your browser, and never shared.',
    icon: BookOpen,
  },
  {
    to: '/app/trauma',
    title: 'Trauma & Healing',
    body: 'Why your body reacted as it did, short videos, grounding tools and UK specialist help.',
    icon: HeartPulse,
  },
  {
    to: '/app/grounding',
    title: 'Grounding Exercises',
    body: 'Four short techniques to bring you back to the present when you feel panicked or stuck.',
    icon: Leaf,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen tf-hero-gradient">
      <SEO
        description="A trauma-informed guide for survivors of sexual violence and domestic abuse in England, Wales and Scotland. Justice process, your rights, helplines, safety planning, grounding tools and a private journal — read anonymously in your browser."
        path="/"
      />
      <FloatingQuickExit />
      <AccessibilityFAB />
      {/* Top bar */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" style={{ textDecoration: 'none' }}>
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
            }}
          >
            <span className="font-heading text-white text-lg" style={{ lineHeight: 1, paddingTop: 2 }}>
              F
            </span>
          </div>
          <span
            className="font-body font-bold tracking-[0.18em]"
            style={{ fontSize: 11, color: 'var(--tf-purple-dark)' }}
          >
            THE FULL IMPACT
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/app/help-now"
            data-testid="header-help-now"
            className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple-dark)',
              border: '1px solid var(--tf-purple-light)',
              textDecoration: 'none',
            }}
          >
            <Phone size={13} /> Find help now
          </Link>
          <TranslateHelper />
          <QuickExitButton />
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-12">
        <div className="tf-fade-up">
          <p className="eyebrow">SURVIVOR-BUILT · ENGLAND, WALES &amp; SCOTLAND</p>
          <h1
            className="font-heading mt-4"
            style={{
              fontSize: 'clamp(32px, 5vw, 54px)',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              color: 'var(--tf-text)',
            }}
          >
            The trauma-informed guide for{' '}
            <span style={{ color: 'var(--tf-purple-dark)' }}>England</span>,{' '}
            <span style={{ color: 'var(--tf-purple-dark)' }}>Wales</span> and{' '}
            <span style={{ color: 'var(--tf-purple-dark)' }}>Scotland</span> —{' '}
            <span style={{ color: 'var(--tf-purple-dark)' }}>sexual violence</span> and{' '}
            <span style={{ color: 'var(--tf-purple-dark)' }}>domestic abuse</span>.
          </h1>
          <p
            className="font-body mt-5 max-w-2xl"
            style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--tf-text-secondary)' }}
          >
            A calm, private companion to help you understand the process, know your rights,
            find specialist support, and gently work through what you&apos;re feeling.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/app/help-now" data-testid="cta-find-help" className="tf-btn-primary">
              <Phone size={16} /> Find help now
            </Link>
          </div>
        </div>

        {/* Wide, thin watercolour banner spanning the full hero width */}
        <div
          className="relative overflow-hidden mt-10"
          style={{
            borderRadius: 20,
            width: '100%',
            aspectRatio: '6 / 1',
            maxHeight: 180,
            boxShadow: '0 14px 36px rgba(139,123,177,0.18)',
          }}
        >
          <img
            src={HERO_IMAGE}
            alt="Soft abstract watercolour background"
            className="w-full h-full object-cover"
            style={{ display: 'block', objectPosition: 'center 40%' }}
          />
          <div
            className="absolute inset-0 flex items-center justify-center px-6"
            style={{
              background:
                'linear-gradient(90deg, rgba(253,252,254,0.55) 0%, rgba(253,252,254,0.25) 50%, rgba(253,252,254,0.55) 100%)',
            }}
          >
            <p
              className="font-heading text-center"
              style={{
                fontSize: 'clamp(16px, 2vw, 22px)',
                lineHeight: 1.25,
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                textShadow: '0 1px 0 rgba(255,255,255,0.4)',
              }}
            >
              You are believed.{' '}
              <span style={{ color: 'var(--tf-purple-dark)' }}>You are not alone.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Two themed doorway sections */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-6">
        <p className="eyebrow">EVERYTHING IS HERE — NO DOWNLOAD</p>
        <h2
          className="font-heading mt-3"
          style={{ fontSize: 'clamp(26px, 3.2vw, 36px)', lineHeight: 1.1, color: 'var(--tf-text)' }}
        >
          Calm doorways<br />into what you need.
        </h2>
        <p
          className="font-body mt-4 max-w-2xl"
          style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--tf-text-secondary)' }}
        >
          Choose the topic that fits — sexual violence on one side, domestic abuse on the other.
          The judicial process, your rights and helplines live inside each. Some doorways below
          are shared because they help with both.
        </p>
      </section>

      {/* SEXUAL VIOLENCE */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14">
        <div
          className="flex items-center gap-3"
          style={{
            paddingBottom: 16,
            borderBottom: '1px solid var(--tf-card-border)',
          }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--tf-blue-soft)', color: 'var(--tf-purple-dark)' }}
          >
            <HeartPulse size={20} />
          </div>
          <div>
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              SECTION 01
            </p>
            <h3
              className="font-heading"
              style={{ fontSize: 'clamp(22px, 2.5vw, 28px)', color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              Sexual violence
            </h3>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SV_DOORWAYS.map((f) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to={f.to}
                data-testid={`landing-sv-${f.to.replace(/[^a-z0-9]/gi, '-')}`}
                className="p-6 rounded-2xl group transition-all"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  textDecoration: 'none',
                  border: '1px solid transparent',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: '#fff', color: 'var(--tf-purple-dark)' }}
                >
                  <Icon size={20} />
                </div>
                <h4
                  className="font-heading"
                  style={{ fontSize: 18, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
                >
                  {f.title}
                </h4>
                <p
                  className="font-body mt-2"
                  style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
                >
                  {f.body}
                </p>
                <span
                  className="inline-flex items-center gap-1 mt-3 font-bold"
                  style={{ color: 'var(--tf-purple-dark)', fontSize: 13 }}
                >
                  Read it <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* DOMESTIC ABUSE */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16">
        <div
          className="flex items-center gap-3"
          style={{
            paddingBottom: 16,
            borderBottom: '1px solid var(--tf-card-border)',
          }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--tf-pink-soft)', color: 'var(--tf-purple-dark)' }}
          >
            <Shield size={20} />
          </div>
          <div>
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              SECTION 02
            </p>
            <h3
              className="font-heading"
              style={{ fontSize: 'clamp(22px, 2.5vw, 28px)', color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              Domestic abuse
            </h3>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {DA_DOORWAYS.map((f) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to={f.to}
                data-testid={`landing-da-${f.to.replace(/[^a-z0-9]/gi, '-')}`}
                className="p-6 rounded-2xl group transition-all"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  textDecoration: 'none',
                  border: '1px solid transparent',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: '#fff', color: 'var(--tf-purple-dark)' }}
                >
                  <Icon size={20} />
                </div>
                <h4
                  className="font-heading"
                  style={{ fontSize: 18, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
                >
                  {f.title}
                </h4>
                <p
                  className="font-body mt-2"
                  style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
                >
                  {f.body}
                </p>
                <span
                  className="inline-flex items-center gap-1 mt-3 font-bold"
                  style={{ color: 'var(--tf-purple-dark)', fontSize: 13 }}
                >
                  Read it <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SHARED TOOLS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <div
          className="flex items-center gap-3"
          style={{
            paddingBottom: 16,
            borderBottom: '1px solid var(--tf-card-border)',
          }}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--tf-purple-light)', color: 'var(--tf-purple-dark)' }}
          >
            <Leaf size={20} />
          </div>
          <div>
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              FOR BOTH
            </p>
            <h3
              className="font-heading"
              style={{ fontSize: 'clamp(22px, 2.5vw, 28px)', color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              Shared tools &amp; healing
            </h3>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-5">
          {SHARED_TOOLS.map((f) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to={f.to}
                data-testid={`landing-shared-${f.to.split('/').pop()}`}
                className="p-6 rounded-2xl group transition-all"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  textDecoration: 'none',
                  border: '1px solid transparent',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: '#fff', color: 'var(--tf-purple-dark)' }}
                >
                  <Icon size={20} />
                </div>
                <h4
                  className="font-heading"
                  style={{ fontSize: 18, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
                >
                  {f.title}
                </h4>
                <p
                  className="font-body mt-2"
                  style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
                >
                  {f.body}
                </p>
                <span
                  className="inline-flex items-center gap-1 mt-3 font-bold"
                  style={{ color: 'var(--tf-purple-dark)', fontSize: 13 }}
                >
                  Read it <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>

        <Link
          to="/app"
          data-testid="cta-explore-app"
          className="inline-flex items-center gap-2 mt-12"
          style={{
            color: 'var(--tf-purple-dark)',
            fontWeight: 700,
            textDecoration: 'none',
            fontSize: 14,
          }}
        >
          See everything in one place <ArrowRight size={14} />
        </Link>
      </section>

      {/* Compact promise strip — small, near the bottom */}
      <section
        className="border-y"
        style={{ background: '#fff', borderColor: 'var(--tf-card-border)' }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { icon: Globe, title: 'Read in your browser', body: 'Nothing to download.' },
            { icon: LockKeyhole, title: 'Private by design', body: 'No account, no sign-up.' },
            { icon: Library, title: 'UK-specific guidance', body: "Rape Crisis · CPS · NHS · gov.uk." },
            { icon: HeartHandshake, title: 'Trauma-informed', body: 'Calm pacing · Quick exit.' },
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="flex items-start gap-2.5 p-3 rounded-xl"
              style={{ background: 'var(--tf-bg-secondary)' }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#fff', color: 'var(--tf-purple-dark)' }}
              >
                <Icon size={15} />
              </div>
              <div className="min-w-0">
                <p
                  className="font-body font-bold"
                  style={{ fontSize: 12.5, color: 'var(--tf-text)', lineHeight: 1.25 }}
                >
                  {title}
                </p>
                <p
                  className="font-body"
                  style={{
                    fontSize: 11,
                    color: 'var(--tf-text-secondary)',
                    marginTop: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* A note from Angela — editorial link before the final CTA */}
      <section className="px-5 sm:px-8 py-14" style={{ background: '#FFFDFA' }}>
        <div className="max-w-2xl mx-auto">
          <Link
            to="/app/why"
            data-testid="landing-why-card"
            className="block rounded-2xl p-6 sm:p-7 transition-shadow"
            style={{
              background: '#fff',
              border: '1.5px solid var(--tf-purple-light)',
              boxShadow: '0 6px 20px rgba(139,123,177,0.08)',
              textDecoration: 'none',
              color: 'var(--tf-text)',
            }}
          >
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.26em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              A NOTE FROM ANGELA
            </p>
            <h3
              className="mt-2"
              style={{
                fontSize: 'clamp(22px, 3vw, 28px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.015em',
                lineHeight: 1.2,
                fontFamily: 'Fraunces, Georgia, serif',
                fontWeight: 500,
              }}
            >
              Information, autonomy and informed choice.
            </h3>
            <p
              className="font-body mt-3"
              style={{
                fontSize: 14,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.6,
              }}
            >
              Why I created The Full Impact — and why every survivor should be given
              clear written information they can take away, read privately, and return
              to at their own pace.
            </p>
            <p
              className="font-body mt-4 inline-flex items-center gap-1.5"
              style={{ fontSize: 13.5, color: 'var(--tf-purple-dark)', fontWeight: 700 }}
            >
              Read the note <ArrowRight size={14} />
            </p>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: 'var(--tf-purple)' }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2
            className="font-heading"
            style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', lineHeight: 1.15, color: '#fff' }}
          >
            Whatever brought you here,<br />you are welcome.
          </h2>
          <p
            className="font-body mt-5 mx-auto"
            style={{ fontSize: 15.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.92)', maxWidth: 540 }}
          >
            Take what helps. Leave what doesn&apos;t. There is no right way to use this — go gently
            with yourself.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/app/help-now"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold"
              style={{ background: '#fff', color: 'var(--tf-purple-dark)', textDecoration: 'none' }}
            >
              <Phone size={16} /> Find help now
            </Link>
          </div>
        </div>
      </section>
      </main>

      <footer
        className="py-10 px-5"
        style={{ background: 'var(--tf-bg)', borderTop: '1px solid var(--tf-card-border)' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <a
            href="https://www.instagram.com/thefullimpactcampaign?igsh=cHEyd2Jjbm9jMjhx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="landing-instagram"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold mb-5"
            style={{
              background: '#fff',
              color: 'var(--tf-purple-dark)',
              border: '1px solid var(--tf-purple-light)',
              fontSize: 13.5,
              textDecoration: 'none',
            }}
          >
            <Instagram size={15} />
            Follow @thefullimpactcampaign
          </a>

          {/* Petition card removed by author request */}

          {/* Share this site card — for ISVAs, IDVAs, social workers */}
          <div className="max-w-xl mx-auto mb-8 text-left">
            <ShareCard />
          </div>

          <p className="font-body" style={{ fontSize: 12, color: 'var(--tf-text-muted)', lineHeight: 1.6 }}>
            © {new Date().getFullYear()} <strong style={{ color: 'var(--tf-text-secondary)' }}>Angela Moreton</strong>. All rights reserved.
            {' · '}The Full Impact™ — a trauma-informed survivor guide for England, Wales and Scotland.
          </p>
          <p
            className="font-body mt-3 mx-auto"
            style={{ fontSize: 11, color: 'var(--tf-text-muted)', lineHeight: 1.65, maxWidth: 680 }}
          >
            Original writing, design and selection of resources by Angela Moreton. Content licensed under{' '}
            <a
              href="https://creativecommons.org/licenses/by-nc-nd/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="tf-link"
            >
              CC BY-NC-ND 4.0
            </a>
            {' '}— share freely with attribution, not commercially and not modified. Quoted material from
            Rape Crisis, CPS, NHS and gov.uk remains the copyright of its respective owners.
          </p>
          <nav aria-label="Footer navigation">
            <p className="font-body mt-3" style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}>
              Anonymous &amp; free · Read in your browser · Quick exit on every page · {' '}
              <a href="/app/why" data-testid="landing-footer-why" className="tf-link">Why I created this</a>{' '}·{' '}
              <a href="/app/about" data-testid="landing-footer-about" className="tf-link">About &amp; Privacy</a>{' '}·{' '}
              <a href="/app/accessibility" data-testid="landing-footer-a11y" className="tf-link">Accessibility statement</a>{' '}·{' '}
              <a href="/app/compliance" data-testid="landing-footer-compliance" className="tf-link">Compliance summary</a>{' '}·{' '}
              <a href="/app/faq" data-testid="landing-footer-faq" className="tf-link">FAQ</a>
            </p>
          </nav>
        </div>
      </footer>
    </div>
  );
}
